import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CheckCircleOutlined, LoadingOutlined, VideoCameraOutlined, WarningOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

import { FaceInput } from '@/components/steps/FaceDetection/FaceInput';
import { useCameraAvailable } from '@/hooks/useCameraAvailable';
import { trpcVanillaClient } from '@/lib/trpc';

type PageState = 'loading' | 'capture' | 'completed' | 'error';

const FaceCapture = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const cameraState = useCameraAvailable();

  useEffect(() => {
    if (!sessionId) {
      setPageState('error');
      return;
    }

    const openSession = async () => {
      try {
        await trpcVanillaClient.facetec.openSession.mutate({ sessionId });
        setPageState('capture');
      } catch {
        setPageState('error');
      }
    };

    openSession();
  }, [sessionId]);

  const handleSuccess = useCallback(() => {
    setPageState('completed');
  }, []);

  return (
    <div className='flex min-h-dvh flex-col bg-white'>
      <header className='flex justify-center border-b border-gray-100 px-6 py-4'>
        <img className='h-6' src='https://cdn.eduzzcdn.com/topbar/eduzz-colored.svg' alt='Eduzz' />
      </header>

      <main className='flex flex-1 flex-col items-center justify-center px-6 py-8'>
        {pageState === 'loading' && (
          <div className='flex flex-col items-center gap-4'>
            <Spin indicator={<LoadingOutlined spin className='text-3xl' />} />
            <p className='text-sm text-gray-500'>Conectando sessão...</p>
          </div>
        )}

        {pageState === 'error' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <WarningOutlined className='text-5xl text-red-500' />
            <h1 className='text-xl font-semibold text-gray-900'>Sessão inválida ou expirada</h1>
            <p className='text-sm text-gray-500'>Gere um novo QR Code no computador e tente novamente.</p>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'checking' && (
          <div className='flex flex-col items-center gap-4'>
            <Spin indicator={<LoadingOutlined spin className='text-3xl' />} />
            <p className='text-sm text-gray-500'>Verificando câmera...</p>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'unavailable' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <VideoCameraOutlined className='text-5xl text-red-500' />
            <h1 className='text-xl font-semibold text-gray-900'>Câmera não disponível</h1>
            <p className='text-sm text-gray-500'>
              Não foi possível acessar a câmera deste dispositivo. Verifique as permissões do navegador e tente
              novamente.
            </p>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'available' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-6'>
            <div className='flex flex-col items-center gap-2 text-center'>
              <h1 className='text-xl font-semibold text-gray-900'>Reconhecimento facial</h1>
              <p className='text-sm text-gray-500'>
                Posicione seu rosto no centro da câmera, em um local bem iluminado.
              </p>
            </div>

            <div className='w-full'>
              <FaceInput onSuccess={handleSuccess} sessionId={sessionId} />
            </div>
          </div>
        )}

        {pageState === 'completed' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <CheckCircleOutlined className='text-5xl text-green-500' />
            <h1 className='text-xl font-semibold text-gray-900'>Tudo certo!</h1>
            <p className='text-sm text-gray-500'>
              Reconhecimento facial concluído com sucesso. Você já pode fechar esta página e voltar ao computador.
            </p>
          </div>
        )}
      </main>

      <footer className='px-6 py-4 text-center text-xs text-gray-300'>Eduzz &copy; {new Date().getFullYear()}</footer>
    </div>
  );
};

export default FaceCapture;
